import styled, { css } from 'styled-components';

/** The video attachment player's frame, controls and scrubber. */

export const Container = styled.div<{ $ratio: number; $flush?: boolean }>`
  position: relative;
  /*
   * Fill the bubble, but never get tiny.
   *
   * A bubble is shrink-to-fit, and a percentage width contributes nothing to
   * its max-content size - so width:100% alone collapsed the player to the
   * width of the message text (a one-word message gave a 135px thumbnail).
   * min-width DOES contribute, so it sets the floor the bubble has to grow
   * to, while width:100% lets the player stretch whenever the text makes the
   * bubble wider. The min() keeps it inside the bubble on very narrow screens,
   * where 280px would otherwise overflow.
   */
  width: 100%;
  min-width: min(280px, 100%);
  aspect-ratio: ${({ $ratio }) => $ratio};
  max-height: 280px;
  /* Square when the clip sits flush inside a captioned group - the group's own
     radius rounds the outside, and a radius here would show through it. */
  border-radius: ${({ $flush }) => ($flush ? '0' : '12px')};
  overflow: hidden;
  background: #000;

  video {
    width: 100%;
    height: 100%;
    /* contain, not cover: the box already carries the video's own ratio, so
       the two are identical for a landscape clip - but when max-height clamps
       a portrait one, cover would crop the middle out of it. Letterboxing
       against the black container is what a player is expected to do. */
    object-fit: contain;
    display: block;
  }

  /* Controls stay out of the way while playing, and are always visible when
     paused so there is an obvious way back in. */
  :hover {
    button,
    [data-ui='progress'] {
      opacity: 1;
    }
  }

  /* On a phone the frame around this is capped to keep the clip off both
     walls; the 280px floor would push straight back through it. */
  @media (max-width: 800px) {
    min-width: 0;
  }

  /* The bubble-sized box above would stay 280px wide in fullscreen, so drop
     every constraint and letterbox instead of cropping. */
  :fullscreen,
  :-webkit-full-screen {
    width: 100vw;
    height: 100vh;
    max-width: none;
    max-height: none;
    aspect-ratio: auto;
    border-radius: 0;

    video {
      object-fit: contain;
    }
  }
`;

const roundButton = css`
  position: absolute;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.25s ease;
  padding: 0;
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
`;

export const CenterButton = styled.button<{ $visible: boolean }>`
  ${roundButton}
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};

  svg {
    width: 16px;
    height: 16px;
  }
`;

/** The two corner controls: same 32px disc, stacked down the right edge. */
const cornerButton = css`
  ${roundButton}
  right: 8px;
  width: 32px;
  height: 32px;
`;

export const FullscreenButton = styled.button<{ $visible: boolean }>`
  ${cornerButton}
  top: 8px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};

  svg {
    width: 16px;
    height: 16px;
  }
`;

/* Sits directly under the fullscreen button: 8 top + 32 button + 8 gap. */
export const MuteButton = styled.button<{ $visible: boolean }>`
  ${cornerButton}
  top: 48px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};

  svg {
    width: 13px;
    height: 13px;
  }
`;

export const ProgressWrapper = styled.div<{ $visible: boolean }>`
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: 8px;
  height: 20px;
  display: flex;
  align-items: center;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.25s ease;
  cursor: pointer;
  /* Let the custom drag win over the browser's scroll gesture on touch. */
  touch-action: none;
`;

export const Track = styled.div`
  position: relative;
  width: 100%;
  height: 4px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.35);
`;

const trackFill = css`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  border-radius: 9999px;
  pointer-events: none;
`;

export const Buffered = styled.div`
  ${trackFill}
  background: rgba(255, 255, 255, 0.55);
`;

export const Played = styled.div`
  ${trackFill}
  background: ${({ theme }) => theme.primary};
`;

export const Thumb = styled.div`
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  transform: translate(-50%, -50%);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  pointer-events: none;
`;

export const TimeLabel = styled.div<{ $visible: boolean }>`
  position: absolute;
  left: 10px;
  bottom: 26px;
  font-size: 11px;
  line-height: 14px;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.25s ease;
  pointer-events: none;
`;

/** Shown instead of the player when the browser can't decode this container. */
export const UnplayableOverlay = styled.a`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-decoration: none;
  text-align: center;
  padding: 12px;
  color: #fff;
  background: rgba(0, 0, 0, 0.45);
  font-size: 12px;
  line-height: 16px;

  span:last-child {
    text-decoration: underline;
    font-weight: 600;
  }
`;
